import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import { useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { useI18n } from "../i18n";

export function Notice({ tone = "info", children, title }: { tone?: "info" | "warn" | "error" | "success"; children: ReactNode; title?: string }) {
  const Icon = tone === "warn" ? AlertTriangle : tone === "error" ? XCircle : tone === "success" ? CheckCircle2 : Info;
  return (
    <div className={`notice ${tone}`} role={tone === "error" ? "alert" : "status"}>
      <Icon size={16} aria-hidden />
      <div className="stack" style={{ gap: 4 }}>
        {title && <strong>{title}</strong>}
        <div>{children}</div>
      </div>
    </div>
  );
}

/** Persistent contextual API feedback; successful actions use the shared Toaster. */
export function ErrorNotice({ code, requestId }: { code: string | null | undefined; requestId?: string }) {
  const { errorText, t } = useI18n();
  if (!code) return null;
  return <Notice tone="error"><p>{errorText(code)}</p>{requestId && <p className="small">{t("common.requestId")}: {requestId}</p>}</Notice>;
}

export function Field({ label, hint, error, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; error?: string | null }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} aria-invalid={error ? true : undefined} aria-describedby={hint || error ? `${id}-d` : undefined} {...props} />
      {(hint || error) && <span id={`${id}-d`} className="hint" style={error ? { color: "var(--danger-ink)" } : undefined}>{error ?? hint}</span>}
    </div>
  );
}

export function TextArea({ label, hint, error, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hint?: string; error?: string | null }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <textarea id={id} aria-invalid={error?true:undefined} aria-describedby={hint||error?`${id}-d`:undefined} {...props} />
      {(hint||error)&&<span id={`${id}-d`} className={`hint ${error?"field-error":""}`}>{error??hint}</span>}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useI18n();
  return <span className="spinner" role="status" aria-label={label ?? t("common.loading")} />;
}

export function Loading() {
  return (
    <div className="center-state">
      <Spinner />
    </div>
  );
}

export function Modal({ open, onOpenChange, title, description, children }: { open: boolean; onOpenChange(v: boolean): void; title: string; description?: string; children: ReactNode }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog" aria-describedby={description ? undefined : undefined}>
          <Dialog.Title asChild>
            <h2>{title}</h2>
          </Dialog.Title>
          {description ? <Dialog.Description className="muted">{description}</Dialog.Description> : <Dialog.Description className="sr-only">{title}</Dialog.Description>}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const tone = ({ active: "success", succeeded: "success", processed: "success", canceled: "warn", past_due: "warn", pending: "accent", incomplete: "accent", received: "accent", expired: "", failed: "danger", refunded: "warn", partially_refunded: "warn", ignored: "" } as Record<string, string>)[status] ?? "";
  return <span className={`badge ${tone}`}>{t(`status.${status}`)}</span>;
}
